import React, { Fragment, PureComponent } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { OverlayView } from 'react-google-maps';

import Websockets from 'services/websocket';
import MapBase from 'components/Map';
import Marker from 'components/Marker';
import Modal from 'components/Modal';
import DeviceMarker from 'components/DeviceMarker';
import { useMap } from 'contexts/MapContext';

import { MapOptions } from './MapOptions';
import { assetData } from './trialMock';
import { MapAssets } from './MapAssets';
import { TrailPolylines } from './TrailPolylines';

import { carMarkerIconSrc, ClearPathLogoBlue, destinationMarkerIconSrc } from 'assets';
import './styles.scss';

interface IMatchParams {
  id: string;
  signature: string;
}

const getPixelPositionOffset = () => ({
  x: -58,
  y: 0,
});

interface MapInnerProps extends RouteComponentProps<IMatchParams> {
  mapState: ReturnType<typeof useMap>['state'];
  mapActions: Omit<ReturnType<typeof useMap>, 'state'>;
}

class MapInner extends PureComponent<MapInnerProps> {
  private readonly map: React.RefObject<any>;

  constructor(props: MapInnerProps) {
    super(props);
    this.map = React.createRef();
  }

  handleZoomChanged = () => {
    if (this.map.current) {
      const currentZoom = this.map.current.getZoom();
      if (currentZoom !== this.props.mapState.zoom) {
        this.props.mapActions.setZoom(currentZoom);
      }
    }
  };

  fetchDetails = async () => {
    const {
      match: {
        params: { id, signature },
      },
    } = this.props;

    const data = await axios.get(
      `${process.env.REACT_APP_API_HOST}/v1.0/clearshare/details?id=${id}&signature=${signature}`
    );

    console.log('Fetching details for ClearShare link:', data, assetData);

    return { data: data.data };
  };

  setDetails = (
    locationData: any,
    destinationData: any,
    jwt: string,
    companyName: string,
    assetsData: any[],
    skipBounds = false
  ) => {
    const { mapActions } = this.props;
    const bounds: any[] = [];
    const location = locationData && {
      lat: locationData.latitude,
      lng: locationData.longitude,
    };
    const destination = destinationData && {
      lat: destinationData.latitude,
      lng: destinationData.longitude,
    };

    const assets =
      assetsData?.map((asset) => ({
        id: asset.id,
        gtsId: asset.gtsId,
        name: asset.name,
        lat: asset.location.latitude,
        lng: asset.location.longitude,
        statusCodeColor: asset.location.statusCodeColor,
        trail:
          asset.trail.map(({ latitude, longitude, timestamp }: any) => {
            return {
              lat: latitude,
              lng: longitude,
              timestamp,
            };
          }) || [],
      })) || [];

    if (destination?.lat && destination?.lng) {
      bounds.push(location);
      bounds.push(destination);
    }

    assets.forEach((asset) => {
      if (asset.lat && asset.lng) {
        bounds.push({
          lat: asset.lat,
          lng: asset.lng,
        });
      }
    });

    mapActions.setMapDetails({
      jwt,
      zoom: 15,
      location,
      destination,
      bounds: bounds.length && !skipBounds ? bounds : null,
      companyName,
      assets,
    });
  };

  async componentDidMount(): Promise<void> {
    const { mapActions } = this.props;

    try {
      const response = (await this.fetchDetails()) as any;
      const data = response.data;

      this.setDetails(data.location, data.destination, data.token, data.companyName, data.assets, false);

      const websocket = new Websockets(data.token as string);
      if (data.deviceId) {
        websocket.connectWS(
          () => {
            console.log('Websocket is connected');
            websocket.subscribeTo(`/events/device/${data.accountId}/${data.deviceId}`, (res: any) => {
              if (res) {
                const event = JSON.parse(res.body);
                mapActions.setLocation({
                  lat: +event.latitude,
                  lng: +event.longitude,
                });
                mapActions.setZoom(15);
              }
            });
          },
          (error: any) => {
            if (error?.body.includes('401')) {
              websocket.disconnectWS();
              mapActions.setExpired(true);
            }
          }
        );
      } else if (data.assetGroup && data.assetGroup.gtsId && data.accountId) {
        websocket.connectWS(
          () => {
            console.log('Websocket is connected');
            websocket.subscribeTo(`/events/group/${data.accountId}/${data.assetGroup.gtsId}`, (res: any) => {
              if (res) {
                const event = JSON.parse(res.body);
                mapActions.updateAssetLocation(
                  event.deviceId,
                  Number(event.latitude),
                  Number(event.longitude),
                  event.timestamp,
                  event.statusColor
                );
              }
            });
          },
          (error: any) => {
            if (error?.body.includes('401')) {
              websocket.disconnectWS();
            }
          }
        );
      }
    } catch (e) {
      mapActions.setExpired(true);
    }
  }

  render() {
    const { mapState, mapActions } = this.props;
    const {
      jwt,
      zoom,
      bounds,
      location,
      destination,
      expired,
      companyName,
      assets,
      hoveredAssetId,
      selectedAssetId,
      trailsEnabled,
      activeTrailOption,
      colorTrailsEnabled,
      colorTrailsMode,
    } = mapState;

    const hasAssets = assets.length > 0;

    // Filter visible assets once to avoid duplicate filtering
    const visibleAssets = hasAssets ? assets.filter((asset) => asset.visible !== false) : [];

    return (
      <Fragment>
        {hasAssets && <MapOptions />}
        {hasAssets && <MapAssets />}
        <div className="MapContainer-logo">
          <a
            className="MapContainer-logoLink"
            href="https://www.clearpathgps.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ClearPathLogoBlue className="MapContainer-logoImage" width={150} height={53} />
          </a>
        </div>
        <Modal open={expired} onClose={() => mapActions.setExpired(false)} title="This ClearShare link has expired.">
          Thank you for viewing this vehicle's location. If you need to continue viewing this vehicle's location, please
          request a new link{companyName ? ` through ${companyName}.` : '.'}
        </Modal>
        <MapBase
          bounds={bounds}
          map={this.map}
          center={location}
          zoom={zoom}
          onZoomChanged={this.handleZoomChanged}
          containerElement={<div style={{ height: `100vh`, width: '100%' }} />}
          googleMapURL={process.env.REACT_APP_GOOGLE_MAP_URL || ''}
          loadingElement={<div style={{ height: `100%` }} />}
          mapElement={<div style={{ height: `100%` }} />}
        >
          <TrailPolylines
            assets={assets}
            trailsEnabled={trailsEnabled}
            colorTrailsEnabled={colorTrailsEnabled}
            colorTrailsMode={colorTrailsMode}
            activeTrailOption={activeTrailOption}
            hoveredAssetId={hoveredAssetId}
          />

          {Boolean(jwt) && (
            <Fragment>
              <Marker position={location} icon={carMarkerIconSrc} size={{ x: 101, y: 101 }} />
              {destination?.lng && destination?.lng && (
                <Marker position={destination} icon={destinationMarkerIconSrc} size={{ x: 34, y: 46 }}>
                  <OverlayView
                    position={destination}
                    mapPaneName={OverlayView.MARKER_LAYER}
                    getPixelPositionOffset={getPixelPositionOffset}
                  >
                    <div className="MapContainer-destinationLabel">Destination</div>
                  </OverlayView>
                </Marker>
              )}
              {visibleAssets.map((asset) => {
                const assetLocation = {
                  lat: asset.lat,
                  lng: asset.lng,
                };

                return (
                  <DeviceMarker
                    key={asset.gtsId}
                    position={assetLocation}
                    name={asset.name}
                    statusColor={asset.statusCodeColor}
                    deviceId={asset.gtsId}
                    active={selectedAssetId === asset.id}
                    onClick={() => mapActions.setSelectedAsset(asset.id)}
                    onMouseOver={() => mapActions.setHoveredAsset(asset.id)}
                    onMouseOut={() => mapActions.setHoveredAsset(null)}
                  />
                );
              })}
            </Fragment>
          )}
        </MapBase>
      </Fragment>
    );
  }
}

const MapWithContext: React.FC<RouteComponentProps<IMatchParams>> = (props) => {
  const { state, ...actions } = useMap();

  return <MapInner {...props} mapState={state} mapActions={actions} />;
};

export default MapWithContext;
